import { httpRequest } from '@app/httpRequest.js';
import { clearMapFeatures } from '@app/store/actions.js';
import type { Processor } from '@app/store/middleware/processorMiddleware.js';
import { mapPromise } from '@features/map/hooks/leafletElementHolder.js';
import { mapRefocus, mapToggleLayer } from '@features/map/model/actions.js';
import { toastsAdd } from '@features/toasts/model/actions.js';
import { cancelRegister } from '@shared/cancelRegister.js';
import { objectToURLSearchParams } from '@shared/stringUtils.js';
import { LatLonSchema } from '@shared/types/common.js';
import distance from '@turf/distance';
import { point } from '@turf/helpers';
import z from 'zod';
import { loadWikimediaCommonsMessages } from '../../translations/loadWikimediaCommonsMessages.js';
import {
  WikimediaCommonsPhoto,
  wikimediaCommonsSetPhotos,
} from '../actions.js';
import { ExtMetaValueSchema, pickLang, stripHtml } from './extMetaUtils.js';

const MIN_ZOOM = 13;

const ExtMetaFieldSchema = z
  .object({ value: ExtMetaValueSchema.optional() })
  .optional();

const CommonsPageSchema = z.object({
  pageid: z.number(),
  title: z.string(),
  coordinates: z.array(LatLonSchema).optional(),
  imageinfo: z
    .array(
      z.object({
        thumburl: z.string().optional(),
        thumbwidth: z.number().optional(),
        thumbheight: z.number().optional(),
        descriptionurl: z.string().optional(),
        extmetadata: z
          .object({
            ImageDescription: ExtMetaFieldSchema,
            Artist: ExtMetaFieldSchema,
            LicenseShortName: ExtMetaFieldSchema,
            LicenseUrl: ExtMetaFieldSchema,
            DateTime: ExtMetaFieldSchema,
          })
          .optional(),
      }),
    )
    .optional(),
});

const CommonsResponseSchema = z.object({
  query: z
    .object({
      pages: z.array(CommonsPageSchema).optional(),
    })
    .optional(),
  continue: z.record(z.string(), z.unknown()).optional(),
});

const MAX_DIAGONAL_M = 9000;

function clampBbox(
  north: number,
  west: number,
  south: number,
  east: number,
): { north: number; west: number; south: number; east: number } {
  const diag = distance(point([west, south]), point([east, north]), {
    units: 'meters',
  });

  if (diag <= MAX_DIAGONAL_M) {
    return { north, west, south, east };
  }

  const scale = MAX_DIAGONAL_M / diag;
  const centerLat = (north + south) / 2;
  const centerLon = (east + west) / 2;
  const halfLat = ((north - south) / 2) * scale;
  const halfLon = ((east - west) / 2) * scale;

  return {
    north: centerLat + halfLat,
    west: centerLon - halfLon,
    south: centerLat - halfLat,
    east: centerLon + halfLon,
  };
}

let initial = true;

export const wikimediaCommonsLayerProcessor: Processor = {
  errorKey: 'general.loadError',
  async handle({ getState, dispatch, prevState }) {
    const {
      map: { layers, lat, lon, zoom },
      wikimediaCommons: { photos },
    } = getState();

    const prevMap = prevState.map;

    const ok0 = layers.includes('M');

    const ok = ok0 && zoom >= MIN_ZOOM;

    const prevOk0 = prevMap.layers.includes('M');

    const prevOk = prevOk0 && prevMap.zoom >= MIN_ZOOM;

    if (
      !initial &&
      `${lat},${lon},${ok}` === `${prevMap.lat},${prevMap.lon},${prevOk}`
    ) {
      return;
    }

    initial = false;

    if (!ok) {
      if (prevOk && photos.length) {
        dispatch(wikimediaCommonsSetPhotos([]));
      }

      return;
    }

    try {
      await new Promise<void>((resolve, reject) => {
        const to = window.setTimeout(
          () => {
            cancelRegister.delete(cancelItem);

            resolve();
          },
          prevOk0 === ok0 ? 1000 : 0,
        );

        const cancelItem = {
          cancelActions: [mapRefocus, mapToggleLayer],
          cancel: () => {
            cancelRegister.delete(cancelItem);

            window.clearTimeout(to);

            reject();
          },
        };

        cancelRegister.add(cancelItem);
      });
    } catch {
      return;
    }

    const bb = (await mapPromise).getBounds();

    const clamped = clampBbox(
      bb.getNorth(),
      bb.getWest(),
      bb.getSouth(),
      bb.getEast(),
    );

    const { language } = getState().l10n;

    const res = await httpRequest({
      getState,
      method: 'GET',
      url:
        'https://commons.wikimedia.org/w/api.php?' +
        objectToURLSearchParams({
          origin: '*',
          action: 'query',
          format: 'json',
          formatversion: '2',
          generator: 'geosearch',
          ggsnamespace: '6',
          ggsbbox: `${clamped.north}|${clamped.west}|${clamped.south}|${clamped.east}`,
          ggslimit: 'max',
          ggsprimary: 'all',
          prop: 'coordinates|imageinfo',
          coprimary: 'all',
          colimit: 'max',
          iiprop: 'url|extmetadata',
          iiurlwidth: '120',
          iiextmetadatafilter:
            'ImageDescription|Artist|LicenseShortName|LicenseUrl|DateTime',
          iiextmetadatamultilang: '1',
          iiextmetadatalanguage: language,
        }),
      expectedStatus: 200,
      cancelActions: [mapRefocus, mapToggleLayer],
    });

    const data = CommonsResponseSchema.parse(await res.json());

    const pages = data.query?.pages;

    if (!pages) {
      dispatch(wikimediaCommonsSetPhotos([]));

      return;
    }

    const out: WikimediaCommonsPhoto[] = [];

    for (const page of pages) {
      const coord = page.coordinates?.[0];

      const ii = page.imageinfo?.[0];

      if (!coord || !ii?.thumburl || !ii.descriptionurl) {
        continue;
      }

      const meta = ii.extmetadata;

      out.push({
        pageId: page.pageid,
        title: page.title,
        lat: coord.lat,
        lon: coord.lon,
        thumbUrl: ii.thumburl.replace(/\?.*$/, ''),
        thumbWidth: ii.thumbwidth ?? 120,
        thumbHeight: ii.thumbheight ?? 120,
        descriptionUrl: ii.descriptionurl,
        description: stripHtml(
          pickLang(meta?.ImageDescription?.value, language),
        ),
        artist: stripHtml(pickLang(meta?.Artist?.value, language)),
        license: stripHtml(pickLang(meta?.LicenseShortName?.value, language)),
        licenseUrl: pickLang(meta?.LicenseUrl?.value, language),
        dateTime: stripHtml(pickLang(meta?.DateTime?.value, language)),
      });
    }

    dispatch(wikimediaCommonsSetPhotos(out));

    if (data.continue) {
      dispatch(
        toastsAdd({
          id: 'wikimediaCommons.moreResults',
          messageKey: 'moreResults',
          messageLoader: loadWikimediaCommonsMessages,
          style: 'warning',
          cancelType: [
            clearMapFeatures.type,
            mapRefocus.type,
            mapToggleLayer.type,
          ],
        }),
      );
    }
  },
};
